import React, { useEffect, useRef, useState } from 'react';
import {
  Home,
  Video,
  Folder,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  FileText,
  BookOpen,
  LayoutGrid,
  StickyNote,
  CalendarDays,
  Shield,
  User,
  CreditCard,
  HelpCircle,
  Mail,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { adminService } from '../../../services/admin.service.ts';

function Sidebar() {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const accountActive =
    location.pathname.startsWith('/profile') || location.pathname.startsWith('/my-plan');
  const supportActive =
    location.pathname.startsWith('/faq') || location.pathname.startsWith('/contact');
  const [accountOpen, setAccountOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const accountRef = useRef(null);
  const supportRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    void adminService.checkStatus().then((ok) => {
      if (!cancelled) setIsAdmin(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!accountOpen && !supportOpen) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (accountOpen && accountRef.current && !accountRef.current.contains(target)) {
        setAccountOpen(false);
      }
      if (supportOpen && supportRef.current && !supportRef.current.contains(target)) {
        setSupportOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [accountOpen, supportOpen]);

  const menuItems = [
    { id: 'home', label: 'Panel', icon: Home, path: '/dashboard' },
    { id: 'meetings', label: 'Toplantılar', icon: Video, path: '/plans' },
    { id: 'calendar', label: 'Takvim', icon: CalendarDays, path: '/calendar' },
    { id: 'agenda-pool', label: 'Gündem Havuzu', icon: BookOpen, path: '/agenda-pool' },
    { id: 'recordings', label: 'Kayıtlar', icon: Folder, path: '/recordings' },
    { id: 'summaries', label: 'Toplantı Özetlerim', icon: FileText, path: '/summaries' },
  ];

  const productItems = [
    { label: 'Beyaz Tahtalar', icon: LayoutGrid, path: '/products/whiteboards' },
    { label: 'Notlar', icon: StickyNote, path: '/products/notes' },
    { label: 'Klipler', icon: ExternalLink, path: '/products/clips' },
    { label: 'Dokümanlar', icon: ExternalLink, path: '/products/documents' },
    { label: 'Görevler', icon: ExternalLink, path: '/products/tasks' },
  ];

  const accountItems = [
    { label: 'Profilim', icon: User, path: '/profile' },
    { label: 'Mevcut Planım', icon: CreditCard, path: '/my-plan' },
  ];

  const supportItems = [
    { label: 'Sıkça Sorulan Sorular', icon: HelpCircle, path: '/faq' },
    { label: 'İletişim', icon: Mail, path: '/contact' },
  ];

  const renderDropdownItems = (items, onNavigate) =>
    items.map((item) => {
      const Icon = item.icon;
      const isActive = location.pathname === item.path;
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={onNavigate}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
            isActive
              ? 'bg-purple-600 text-white'
              : 'text-gray-200 hover:bg-white/10 hover:text-white'
          }`}
        >
          <Icon className="w-4 h-4 shrink-0" />
          <span>{item.label}</span>
        </Link>
      );
    });

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-4 xl:p-6 sticky top-20 overflow-visible">
      <nav className="space-y-1 mb-6">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            location.pathname === item.path ||
            (item.path === '/plans' && location.pathname.startsWith('/plans')) ||
            (item.path === '/calendar' && location.pathname.startsWith('/calendar')) ||
            (item.path === '/agenda-pool' && location.pathname.startsWith('/agenda-pool'));
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
                isActive
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="mb-6">
        <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase mb-2">Ürünlerim</h3>
        <div className="space-y-1">
          {productItems.map((item, index) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <Link
                key={index}
                to={item.path}
                className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition ${
                  isActive
                    ? 'bg-purple-600/80 text-white'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
          <Link
            to="/products"
            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-400 hover:text-white rounded-lg transition text-sm"
          >
            <span>Daha Fazla Ürün Keşfedin</span>
          </Link>
        </div>
      </div>

      <div className="space-y-1 border-t border-white/10 pt-4">
        <div className="relative" ref={accountRef}>
          <button
            type="button"
            onClick={() => {
              setAccountOpen((open) => !open);
              setSupportOpen(false);
            }}
            className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition ${
              accountActive || accountOpen
                ? 'text-white bg-white/5'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
            aria-expanded={accountOpen}
            aria-haspopup="menu"
          >
            <span className="flex items-center gap-3 text-sm">
              <ChevronRight
                className={`w-4 h-4 transition-transform ${accountOpen ? 'rotate-90' : ''}`}
              />
              Hesabım
            </span>
            <ChevronDown
              className={`w-4 h-4 opacity-60 transition-transform ${accountOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {accountOpen && (
            <div
              role="menu"
              className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border border-white/10 bg-slate-900 shadow-xl p-1.5 space-y-0.5"
            >
              {renderDropdownItems(accountItems, () => setAccountOpen(false))}
            </div>
          )}
        </div>

        {isAdmin && (
          <Link
            to="/admin"
            className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition ${
              location.pathname.startsWith('/admin')
                ? 'bg-purple-600/80 text-white'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span className="text-sm">Yönetici</span>
          </Link>
        )}

        <div className="relative" ref={supportRef}>
          <button
            type="button"
            onClick={() => {
              setSupportOpen((open) => !open);
              setAccountOpen(false);
            }}
            className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition ${
              supportActive || supportOpen
                ? 'text-white bg-white/5'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
            aria-expanded={supportOpen}
            aria-haspopup="menu"
          >
            <span className="flex items-center gap-3 text-sm">
              <ChevronRight
                className={`w-4 h-4 transition-transform ${supportOpen ? 'rotate-90' : ''}`}
              />
              Destek
            </span>
            <ChevronDown
              className={`w-4 h-4 opacity-60 transition-transform ${supportOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {supportOpen && (
            <div
              role="menu"
              className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border border-white/10 bg-slate-900 shadow-xl p-1.5 space-y-0.5"
            >
              {renderDropdownItems(supportItems, () => setSupportOpen(false))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
